import React, { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { motion } from 'framer-motion';
import { submitTestimonial } from '../../api/directus';

// Regular expressions for content validation
const URL_REGEX = /https?:\/\/(www\.)?[-a-zA-Z0-9@:%._+~#=]{1,256}\.[a-zA-Z0-9()]{1,6}\b([-a-zA-Z0-9()@:%_+.~#?&//=]*)/gi;
const EMAIL_REGEX = /[a-zA-Z0-9._%+-]+@[a-zA-Z0-9.-]+\.[a-zA-Z]{2,}/gi;
const HTML_TAG_REGEX = /<[^>]*>/g;

/**
 * TestimonialForm component
 * Allows users to submit testimonials directly on the website
 * Includes form validation, loading state, and success message
 */
const TestimonialForm = ({ onSuccess }) => {
  const { t } = useTranslation();
  
  // Form state
  const [formData, setFormData] = useState({
    author: '',
    company: '',
    game: '',
    quote: ''
  });
  
  // Form submission state
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [submitSuccess, setSubmitSuccess] = useState(false);
  const [submitError, setSubmitError] = useState(null);
  
  // Form validation state
  const [errors, setErrors] = useState({});
  
  // Handle input changes
  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: value
    }));
    
    // Clear error when user types
    if (errors[name]) {
      setErrors(prev => ({
        ...prev,
        [name]: null
      }));
    }
  };
  
  // Sanitize input to remove HTML tags
  const sanitizeInput = (text) => {
    return text.replace(HTML_TAG_REGEX, '');
  };
  
  // Check if text contains URLs or email addresses
  const containsMaliciousContent = (text) => {
    return URL_REGEX.test(text) || EMAIL_REGEX.test(text);
  };
  
  // Validate form
  const validateForm = () => {
    const newErrors = {};
    
    // Validate author field
    if (!formData.author.trim()) {
      newErrors.author = t('testimonials.form.errorName');
    } else if (containsMaliciousContent(formData.author)) {
      newErrors.author = t('testimonials.form.errorMaliciousContent');
    }
    
    // Validate company field
    if (formData.company && containsMaliciousContent(formData.company)) {
      newErrors.company = t('testimonials.form.errorMaliciousContent');
    }
    
    // Validate game field
    if (formData.game && containsMaliciousContent(formData.game)) {
      newErrors.game = t('testimonials.form.errorMaliciousContent');
    }
    
    // Validate quote field
    if (!formData.quote.trim()) {
      newErrors.quote = t('testimonials.form.errorQuote');
    } else if (formData.quote.length < 20) {
      newErrors.quote = t('testimonials.form.errorQuoteLength');
    } else if (containsMaliciousContent(formData.quote)) {
      newErrors.quote = t('testimonials.form.errorMaliciousContent');
    }
    
    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };
  
  // Handle form submission
  const handleSubmit = async (e) => {
    e.preventDefault();
    
    // Validate form
    if (!validateForm()) {
      return;
    }
    
    try {
      setIsSubmitting(true);
      setSubmitError(null);
      
      // Sanitize all inputs to remove HTML tags
      const sanitizedData = {
        author: sanitizeInput(formData.author),
        company: formData.company ? sanitizeInput(formData.company) : null,
        game: formData.game ? sanitizeInput(formData.game) : null,
        quote: sanitizeInput(formData.quote),
        status: false // Set as false so admin can review before publishing
      };
      
      // Submit testimonial to Directus API
      await submitTestimonial(sanitizedData);
      
      // Send email notification about new testimonial using Cloudflare Worker
      try {
        // Send notification via Cloudflare Worker
        const response = await fetch('https://send-mail.castelnathan66.workers.dev/testimonial', {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
          },
          body: JSON.stringify({
            author: sanitizedData.author,
            company: sanitizedData.company,
            game: sanitizedData.game,
            quote: sanitizedData.quote
          })
        });
        
        const emailResult = await response.json();
        if (!emailResult.success) {
          console.warn('Email notification warning:', emailResult.message);
        }
      } catch (emailError) {
        // Log email error but don't fail the submission
        console.error('Error sending notification email:', emailError);
      }
      
      // Show success message
      setSubmitSuccess(true);
      
      // Reset form
      setFormData({
        author: '',
        company: '',
        game: '',
        quote: ''
      });
      
      // Call onSuccess callback if provided
      if (onSuccess) {
        onSuccess();
      }
    } catch (error) {
      console.error('Error submitting testimonial:', error);
      setSubmitError(t('testimonials.form.errorSubmit'));
    } finally {
      setIsSubmitting(false);
    }
  };
  
  // Animation variants
  const formVariants = {
    hidden: { opacity: 0, y: 20 },
    visible: { 
      opacity: 1, 
      y: 0,
      transition: { 
        duration: 0.6,
        ease: [0.25, 0.1, 0.25, 1]
      }
    }
  };
  
  // Success message component
  const SuccessMessage = () => (
    <motion.div 
      className="bg-green-50 border border-green-200 rounded-md p-6 text-center"
      initial={{ opacity: 0, scale: 0.9 }}
      animate={{ opacity: 1, scale: 1 }}
      transition={{ duration: 0.5 }}
    >
      <div className="text-green-600 text-5xl mb-4">✓</div>
      <h3 className="text-xl font-medium text-green-800 mb-2">
        {t('testimonials.form.successTitle')}
      </h3>
      <p className="text-green-700 mb-4">
        {t('testimonials.form.successMessage')}
      </p>
      <button
        type="button"
        onClick={() => setSubmitSuccess(false)}
        className="inline-block bg-green-600 text-white px-6 py-2 rounded-md transition-all duration-300 hover:bg-green-700"
      >
        {t('testimonials.form.submitAnother')}
      </button>
    </motion.div>
  );
  
  // If submission was successful, show success message
  if (submitSuccess) {
    return <SuccessMessage />;
  }
  
  return (
    <motion.div
      variants={formVariants}
      initial="hidden"
      animate="visible"
    >
      <form onSubmit={handleSubmit} className="space-y-6">
        {/* Error message */}
        {submitError && (
          <div className="bg-red-50 border border-red-200 text-red-700 px-4 py-3 rounded-md">
            {submitError}
          </div>
        )}
        
        {/* Name field */}
        <div>
          <label htmlFor="author" className="block text-primary font-medium mb-2">
            {t('testimonials.form.name')} <span className="text-red-500">*</span>
          </label>
          <input
            type="text"
            id="author"
            name="author"
            value={formData.author}
            onChange={handleChange}
            className={`w-full px-4 py-3 border ${errors.author ? 'border-red-500' : 'border-gray-300'} rounded-md focus:outline-none focus:ring-2 focus:ring-primary/50`}
            disabled={isSubmitting}
          />
          {errors.author && (
            <p className="mt-1 text-red-500 text-sm">{errors.author}</p>
          )}
        </div>
        
        {/* Company field */}
        <div>
          <label htmlFor="company" className="block text-primary font-medium mb-2">
            {t('testimonials.form.company')}
          </label>
          <input
            type="text"
            id="company"
            name="company"
            value={formData.company}
            onChange={handleChange}
            className={`w-full px-4 py-3 border ${errors.company ? 'border-red-500' : 'border-gray-300'} rounded-md focus:outline-none focus:ring-2 focus:ring-primary/50`}
            disabled={isSubmitting}
          />
          {errors.company && (
            <p className="mt-1 text-red-500 text-sm">{errors.company}</p>
          )}
        </div>
        
        {/* Game field */}
        <div>
          <label htmlFor="game" className="block text-primary font-medium mb-2">
            {t('testimonials.form.game')}
          </label>
          <input
            type="text"
            id="game"
            name="game"
            value={formData.game}
            onChange={handleChange}
            className={`w-full px-4 py-3 border ${errors.game ? 'border-red-500' : 'border-gray-300'} rounded-md focus:outline-none focus:ring-2 focus:ring-primary/50`}
            disabled={isSubmitting}
          />
          {errors.game && (
            <p className="mt-1 text-red-500 text-sm">{errors.game}</p>
          )}
        </div>
        
        {/* Testimonial field */}
        <div>
          <label htmlFor="quote" className="block text-primary font-medium mb-2">
            {t('testimonials.form.testimonial')} <span className="text-red-500">*</span>
          </label>
          <textarea
            id="quote"
            name="quote"
            value={formData.quote}
            onChange={handleChange}
            rows="5"
            className={`w-full px-4 py-3 border ${errors.quote ? 'border-red-500' : 'border-gray-300'} rounded-md focus:outline-none focus:ring-2 focus:ring-primary/50`}
            disabled={isSubmitting}
          ></textarea>
          {errors.quote && (
            <p className="mt-1 text-red-500 text-sm">{errors.quote}</p>
          )}
        </div>
        
        {/* Submit button */}
        <div>
          <button
            type="submit"
            disabled={isSubmitting}
            className="w-full bg-primary text-white px-6 py-3 rounded-md transition-all duration-300 hover:bg-primary/90 disabled:opacity-70 disabled:cursor-not-allowed"
          >
            {isSubmitting ? (
              <span className="flex items-center justify-center">
                <svg className="animate-spin -ml-1 mr-3 h-5 w-5 text-white" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
                  <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                  <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                </svg>
                {t('testimonials.form.submitting')}
              </span>
            ) : t('testimonials.form.submit')}
          </button>
        </div>
        
        <p className="text-sm text-gray-500 mt-4">
          {t('testimonials.form.note')}
        </p>
      </form>
    </motion.div>
  );
};

export default TestimonialForm;
